import { Box, Container, Text } from '@everlywell/ui-kit';
import { SectionHeader } from 'components/SectionHeader';
import { GatsbyImage, getImage, IGatsbyImageData } from 'gatsby-plugin-image';
import React from 'react';

interface ContentfulComparisonRow {
  id: string;
  label: string;
  memberAContent: {
    memberAContent: string;
  };
  memberBContent: {
    memberBContent: string;
  };
  colorMemberA?: string;
  colorMemberB?: string;
}

interface ContentfulMember {
  title: string;
  subtitle: string;
  image?: {
    gatsbyImageData: IGatsbyImageData;
    description: string;
  };
}

interface ContentfulComparisonTable {
  memberA: ContentfulMember;
  memberB: ContentfulMember;
  rows: ContentfulComparisonRow[];
  comparisonDisclaimer?: {
    childMarkdownRemark: {
      rawMarkdownBody: string;
    };
  };
}

interface MemberComparisonSectionProps {
  title?: string;
  headline?: string;
  disclaimer?: {
    childMarkdownRemark: {
      html: string;
    };
  };
  container?: Array<ContentfulComparisonTable>;
}

const MemberComparisonSection: React.FC<MemberComparisonSectionProps> = (
  props,
) => {
  const { title, headline, disclaimer } = props;

  // Check if container exists and has at least one item
  if (!props.container || props.container.length === 0) {
    return null;
  }

  const { memberA, memberB, rows } = props.container[0];

  // Check if required data exists
  if (!memberA || !memberB || !rows) {
    return null;
  }

  return (
    <Container
      size="xl"
      py={['4rem', '4rem', '6rem']}
      mx="auto"
      maxWidth="1100px"
      px={[3, 3, 0]}
    >
      <SectionHeader
        eyebrow={title || ''}
        title={headline || ''}
        titleMaxWidth="750px"
      />

      <Box
        bg="white"
        borderRadius={['lg', 'xl']}
        overflow="hidden"
        mt={[6, 8, 12]}
        overflowX="auto"
        sx={{
          WebkitOverflowScrolling: 'touch',
          '::-webkit-scrollbar': { display: 'none' },
          scrollbarWidth: 'none',
        }}
      >
        {/* Header */}
        <Box
          display="grid"
          gridTemplateColumns={[
            '200px 1fr 1fr',
            '250px 1fr 1fr',
            '300px 1fr 1fr',
          ]}
          gap={0}
          mb={0.5}
          minWidth={['500px', '600px', '600px']}
          w="100%"
        >
          <Box p={[2, 2, 3]} backgroundColor="#F9F9F6" />
          <Box p={[2, 2, 3]} backgroundColor="#E8E8E2">
            <Box>
              {memberA.image && memberA.image.gatsbyImageData ? (
                <GatsbyImage
                  image={getImage(memberA.image)!}
                  alt={memberA.image.description || 'Member A'}
                  style={{
                    borderRadius: '12px',
                    marginBottom: '12px',
                    width: '60px',
                    height: '60px',
                  }}
                />
              ) : (
                <Box
                  width="60px"
                  height="60px"
                  borderRadius="12px"
                  backgroundColor="#D9D9D9"
                  mb="12px"
                />
              )}
              <Text fontSize={['xl', 'xl', '2xl']} fontWeight="medium" mb={1}>
                {memberA.title}
              </Text>
              <Text color="#545454" fontSize={['sm', 'sm', 'md']}>
                {memberA.subtitle}
              </Text>
            </Box>
          </Box>
          <Box p={[2, 2, 3]} backgroundColor="#F9F9F6">
            <Box>
              {memberB.image && memberB.image.gatsbyImageData ? (
                <GatsbyImage
                  image={getImage(memberB.image)!}
                  alt={memberB.image.description || 'Member B'}
                  style={{
                    borderRadius: '12px',
                    marginBottom: '12px',
                    width: '60px',
                    height: '60px',
                  }}
                />
              ) : (
                <Box
                  width="60px"
                  height="60px"
                  borderRadius="12px"
                  backgroundColor="#D9D9D9"
                  mb="12px"
                />
              )}
              <Text fontSize={['xl', 'xl', '2xl']} fontWeight="medium" mb={1}>
                {memberB.title}
              </Text>
              <Text color="#545454" fontSize={['sm', 'sm', 'md']}>
                {memberB.subtitle}
              </Text>
            </Box>
          </Box>
        </Box>

        {/* Comparison Rows */}
        <Box
          display="grid"
          gridTemplateRows={`repeat(${rows.length}, 1fr)`}
          minWidth={['500px', '600px', '600px']}
          w="100%"
        >
          {rows.map((row) => (
            <Box
              key={row.id}
              display="grid"
              gridTemplateColumns={[
                '200px 1fr 1fr',
                '250px 1fr 1fr',
                '300px 1fr 1fr',
              ]}
              minHeight={['80px', '90px', '100px']}
              gap={0}
              borderBottom="1px solid"
              borderColor="white"
            >
              <Box
                p={[2, 2, 3]}
                backgroundColor="#F9F9F6"
                display="flex"
                alignItems="center"
              >
                <Text color="#545454" fontSize={['sm', 'sm', 'md']}>
                  {row.label}
                </Text>
              </Box>
              <Box
                p={[2, 2, 3]}
                backgroundColor="#E8E8E2"
                display="flex"
                alignItems="center"
              >
                <Box flex="1">
                  <Text
                    color={row.colorMemberA}
                    fontSize={['sm', 'sm', 'md']}
                    dangerouslySetInnerHTML={{
                      __html: row.memberAContent?.memberAContent || '',
                    }}
                  />
                </Box>
              </Box>
              <Box
                p={[2, 2, 3]}
                backgroundColor="#F9F9F6"
                display="flex"
                alignItems="center"
              >
                <Box flex="1">
                  <Text
                    color={row.colorMemberB}
                    fontSize={['sm', 'sm', 'md']}
                    dangerouslySetInnerHTML={{
                      __html: row.memberBContent?.memberBContent || '',
                    }}
                  />
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>

      {disclaimer && disclaimer.childMarkdownRemark && (
        <Box textAlign="center" mx="auto" mt={6}>
          <Box
            className="disclaimer"
            fontSize="sm"
            color="#545454"
            dangerouslySetInnerHTML={{
              __html: disclaimer.childMarkdownRemark.html,
            }}
            sx={{
              a: {
                color: 'green',
              },
            }}
          />
        </Box>
      )}
    </Container>
  );
};

export default MemberComparisonSection;
